import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Authentication from "../../api/Authentication/Authentication";
import API from "../../api/API";

const Account = () => {
  const { id } = useParams(); // Get the account id from the URL
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [spectating, setSpectating] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // State for updating account details
  const [newUsername, setNewUsername] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    const user = Authentication.getCurrentUser();
    if (user) {
      setCurrentUser(user);
      if (user._id !== id) {
        API.getAccountInfo(id)
          .then((result) => {
            if (result.data.success) {
              setOtherUser(result.data.info);
            } else {
              setError("User not found!");
            }
            setSpectating(true);
            setLoading(false);
          })
          .catch(() => {
            setError("An error occurred while fetching account info.");
            setSpectating(true);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    } else {
      API.getAccountInfo(id)
        .then((result) => {
          if (result.data.success) {
            setOtherUser(result.data.info);
          } else {
            setError("User not found!");
          }
          setSpectating(true);
          setLoading(false);
        })
        .catch(() => {
          setError("An error occurred while fetching account info.");
          setSpectating(true);
          setLoading(false);
        });
    }
  }, [id]);

  // Handle username update
  const handleUpdateUsername = () => {
    if (!newUsername.trim()) return setStatusMessage("Username cannot be empty.");
    API.updateUsername(currentUser._id, newUsername)
      .then((response) => {
        if (response.data.success) {
          setCurrentUser({ ...currentUser, username: newUsername });
          setStatusMessage("Username updated successfully.");
          setNewUsername("");
        } else {
          setStatusMessage(response.data.info || "Failed to update username.");
        }
      })
      .catch(() => setStatusMessage("An error occurred while updating username."));
  };

  // Handle password change
  const handleChangePassword = () => {
    if (!currentPassword || !newPassword)
      return setStatusMessage("Please enter both current and new passwords.");
    API.changePassword(currentUser._id, currentPassword, newPassword)
      .then((response) => {
        if (response.data.success) {
          setStatusMessage("Password changed successfully.");
          setCurrentPassword("");
          setNewPassword("");
        } else {
          setStatusMessage(response.data.info || "Failed to change password.");
        }
      })
      .catch(() => setStatusMessage("An error occurred while changing password."));
  };

  // Handle account deletion
  const handleDeleteAccount = () => {
    if (!window.confirm("Are you sure you want to delete your account? This action is irreversible!")) return;
    API.deleteAccount(currentUser._id)
      .then((response) => {
        if (response.data.success) {
          Authentication.logout();
          navigate("/");
        } else {
          setStatusMessage("Failed to delete account.");
        }
      })
      .catch(() => setStatusMessage("An error occurred while deleting account."));
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-100">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 p-6">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        {!spectating ? (
          <>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Welcome, {currentUser?.username}!</h1>
            <p className="text-gray-600 mb-4">Email: {currentUser?.email}</p>
            <p className="text-gray-600 mb-4">Role: {currentUser?.isAdmin ? "Administrator" : "User"}</p>

            {/* Update Username */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">New Username</label>
              <input
                type="text"
                placeholder="Enter new username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring focus:border-blue-300"
              />
              <button
                onClick={handleUpdateUsername}
                className="mt-2 w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded transition duration-200"
              >
                Update Username
              </button>
            </div>

            {/* Change Password */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">Current Password</label>
              <input
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring focus:border-blue-300"
              />
              <label className="block text-gray-700 font-medium mt-2">New Password</label>
              <input
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring focus:border-blue-300"
              />
              <button
                onClick={handleChangePassword}
                className="mt-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2 rounded transition duration-200"
              >
                Change Password
              </button>
            </div>

            {/* Delete Account */}
            <button
              onClick={handleDeleteAccount}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-2 rounded transition duration-200"
            >
              Delete Account
            </button>

            {statusMessage && <p className="text-center mt-4 text-green-500">{statusMessage}</p>}
          </>
        ) : otherUser ? (
          <h1 className="text-2xl font-bold text-gray-800">Viewing Account: {otherUser.username}</h1>
        ) : (
          <p className="text-red-500 font-medium">{error}</p>
        )}
      </div>
    </div>
  );
};

export default Account;